import { useEffect, useMemo, useState } from 'react'
import cn from 'classnames'
import { CADENCE_PRESETS } from '@forwardflow/features/campaign-flow/data/mock'
import { StepFooter } from '@forwardflow/features/campaign-flow/components/frame/FlowFrame'
import {
   CadenceTimeline,
   TouchDetails,
} from '@forwardflow/features/campaign-flow/components/playbook/CadenceTimeline'
import { ContentPicker } from '@forwardflow/features/campaign-flow/components/playbook/ContentPicker'
import { LeadPhoneDrawer } from '@forwardflow/features/campaign-flow/components/phone/LeadPhoneDrawer'
import {
   FlowIcon,
   TFlowIcon,
} from '@forwardflow/features/campaign-flow/components/ui/FlowIcon'
import {
   AddRow,
   EditableText,
   SmallButton,
} from '@forwardflow/features/campaign-flow/components/ui/primitives'
import {
   useFill,
   useSpeechFill,
} from '@forwardflow/features/campaign-flow/hooks/useFlowHelpers'
import { useTestRun } from '@forwardflow/features/campaign-flow/hooks/useTestRun'
import {
   IPlaybook,
   ITouch,
   TCadencePreset,
   TContentSlot,
   TEditTab,
   TLeadType,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import {
   activeType,
   useCampaignFlowStore,
   usePlaybookView,
} from '@forwardflow/features/campaign-flow/store/flow.store'
import {
   LEAD_TYPE_LABEL,
   audienceTypes,
} from '@forwardflow/features/campaign-flow/utils/describe'
import {
   applyPreset,
   changedTypes,
   contentOf,
   nextDay,
   sortTouches,
   syncMessages,
   viewFor,
} from '@forwardflow/features/campaign-flow/utils/playbook'
import {
   plural,
   smsSegments,
} from '@forwardflow/features/campaign-flow/utils/text'

const TABS: { key: TEditTab; icon: TFlowIcon; label: string }[] = [
   { key: 'cadence', icon: 'cal', label: 'Timing' },
   { key: 'script', icon: 'phone', label: 'Call script' },
   { key: 'texts', icon: 'chat', label: 'Texts' },
   { key: 'voicemails', icon: 'vm', label: 'Voicemails' },
]

const uid = (p: string) =>
   `${p}${Date.now().toString(36)}${Math.floor(Math.random() * 1e4)}`
type TUpdate = (fn: (p: IPlaybook) => IPlaybook) => void

/**
 * The playbook, opened from the rail only if the user wants to change it.
 * One screen, four tabs. "Try it as a lead" opens the test phone in a side
 * drawer that stays open across tabs and jumps to whatever is being edited.
 */
export const PlaybookEditor = ({
   onDone,
   onBack,
   busy,
}: {
   onDone: () => void
   /** Leave without saving; unsaved changes are dropped after a confirmation. */
   onBack: () => void
   busy: boolean
}) => {
   const {
      draft,
      editTab,
      setEditTab,
      updatePlaybook,
      playbookBefore,
      discardEdits,
      phoneOpen,
      setPhoneOpen,
      setEditType,
      typePicked,
      pickTypeAgain,
   } = useCampaignFlowStore()
   const pb = usePlaybookView()
   const type = useCampaignFlowStore(activeType)
   const types = audienceTypes(draft.audience)
   const say = useSpeechFill()
   const run = useTestRun(pb, say)
   const changed = useMemo(
      () =>
         !!playbookBefore &&
         JSON.stringify(playbookBefore) !== JSON.stringify(draft.playbook),
      [playbookBefore, draft.playbook]
   )
   /* Lead types whose words differ from when the editor opened. */
   const edited = useMemo(
      () =>
         playbookBefore
            ? changedTypes(playbookBefore, draft.playbook, types)
            : [],
      [playbookBefore, draft.playbook, types]
   )
   /* Which library item each tab shows, and whether its words were changed since it was picked. */
   const slot: TContentSlot = editTab === 'script' ? 'script' : 'messages'
   const [picked, setPicked] = useState<Record<string, string>>({})
   /* The library item's name when it was picked or the editor opened, to propose a name for the user's copy. */
   const [pickedName, setPickedName] = useState<Record<string, string>>({})
   const before = playbookBefore ? viewFor(playbookBefore, type) : null
   const slotEdited =
      slotWords(pb, slot) !==
      (picked[`${type}:${slot}`] ??
         (before ? slotWords(before, slot) : slotWords(pb, slot)))
   const nameKey = slot === 'script' ? 'scriptName' : 'messageGroupName'
   const ownName = pb[nameKey]
   const baseName =
      pickedName[`${type}:${slot}`] ?? (before ? before[nameKey] : pb[nameKey])
   const campaignName = draft.audience.name.trim() || 'this campaign'
   useEffect(() => {
      /* The first change to a library item's words proposes "<item> · <campaign>" as the copy's name. */
      if (slotEdited && pb.origin !== 'mine' && ownName === baseName)
         updatePlaybook((p) => ({
            ...p,
            [nameKey]: `${baseName} · ${campaignName}`,
         }))
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [slotEdited])
   const badge: Record<TEditTab, number> = {
      cadence: pb.touches.length,
      script: pb.script.length + (pb.opening?.trim() ? 1 : 0),
      texts: pb.texts.length,
      voicemails: pb.voicemails.length,
   }
   /** When the phone is open, jump to the touch being edited. */
   const focus = (key: string) =>
      phoneOpen && run.items.some((i) => i.key === key) && run.startAt(key)
   const openPhone = () => {
      setPhoneOpen(true)
      if (!run.item) run.start()
   }
   /** Several lead types: back to "whose playbook?"; else back out of the editor. */
   const back = () => {
      if (types.length > 1) {
         setPhoneOpen(false)
         pickTypeAgain()
         return
      }
      if (
         changed &&
         !window.confirm(
            'Leave the playbook? Your changes here will be dropped.'
         )
      )
         return
      onBack()
   }

   /* Several lead types: first choose whose playbook to open. */
   if (types.length > 1 && !typePicked)
      return (
         <LeadTypeChooser
            types={types}
            playbook={draft.playbook}
            edited={edited}
            onPick={(t) => {
               setEditType(t)
               setEditTab('script')
            }}
            onDone={onDone}
            busy={busy}
            changed={changed}
         />
      )

   return (
      <>
         <div className="px-5 md:px-8 pt-3 pb-4 flex flex-col gap-3">
            {/* One short line: back, title, note, the phone. The tabs sit right under it. */}
            <div
               className="flex items-center gap-3 min-w-0"
               data-testid="cf-playbook-head"
            >
               <button
                  type="button"
                  onClick={back}
                  aria-label={types.length > 1 ? 'All lead types' : 'Back'}
                  title={types.length > 1 ? 'All lead types' : 'Back'}
                  className="w-8 h-8 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center shrink-0"
                  data-testid={
                     types.length > 1
                        ? 'cf-pick-type-again'
                        : 'cf-playbook-back'
                  }
               >
                  <FlowIcon name="arrowLeft" size={15} />
               </button>
               <div className="flex-1 min-w-0 flex items-baseline gap-x-2 flex-wrap">
                  <h1
                     className="text-[15px] leading-6 font-semibold tracking-[-.01em] truncate"
                     data-testid="cf-playbook-title"
                  >
                     Playbook
                     {types.length > 1 ? ` · ${LEAD_TYPE_LABEL[type]}` : ''}
                  </h1>
                  <span className="text-xs leading-5 text-[#9ca3af] truncate hidden md:inline">
                     {pb.origin === 'mine'
                        ? 'Your version. Changes apply to this campaign only.'
                        : 'Our best performing calls and texts. Change anything you like.'}
                  </span>
               </div>
               <SmallButton
                  icon="phone"
                  variant="outline"
                  className="!h-8 !px-3 !text-[13px]"
                  onClick={() =>
                     phoneOpen ? setPhoneOpen(false) : openPhone()
                  }
                  testId="cf-try-lead"
               >
                  {phoneOpen ? 'Hide phone' : 'Try it as a lead'}
               </SmallButton>
            </div>
            <div
               className="grid grid-cols-2 sm:grid-cols-4 gap-1 bg-white/[0.04] border border-white/[0.06] rounded-xl p-1"
               role="tablist"
               data-testid="cf-edit-tabs"
            >
               {TABS.map((t) => (
                  <button
                     key={t.key}
                     type="button"
                     role="tab"
                     aria-selected={editTab === t.key}
                     onClick={() => setEditTab(t.key)}
                     data-testid={`cf-tab-${t.key}`}
                     className={cn(
                        'h-10 rounded-lg flex items-center justify-center gap-2 text-[13px] whitespace-nowrap transition-colors',
                        editTab === t.key
                           ? 'bg-[#9061DA] text-white'
                           : 'text-[#9ca3af] hover:text-white hover:bg-white/[0.05]'
                     )}
                  >
                     <FlowIcon name={t.icon} size={14} />
                     <b className="font-semibold">{t.label}</b>
                     <span
                        className={cn(
                           'text-[11px] font-semibold px-1.5 rounded-full',
                           editTab === t.key ? 'bg-white/20' : 'bg-white/[0.08]'
                        )}
                     >
                        {badge[t.key]}
                     </span>
                  </button>
               ))}
            </div>
            {editTab !== 'cadence' && (
               <ContentPicker
                  key={`${type}-${slot}`}
                  type={type}
                  slot={slot}
                  currentId={
                     slot === 'script' ? pb.scriptId : pb.messageGroupId
                  }
                  currentName={
                     slot === 'script' ? pb.scriptName : pb.messageGroupName
                  }
                  edited={slotEdited}
                  onPick={(content, option) => {
                     updatePlaybook((p) =>
                        syncMessages({
                           ...p,
                           ...content,
                           origin: option.owner === 'yours' ? 'mine' : p.origin,
                        })
                     )
                     setPicked((x) => ({
                        ...x,
                        [`${type}:${slot}`]: slotWords(
                           { ...pb, ...content } as IPlaybook,
                           slot
                        ),
                     }))
                     setPickedName((x) => ({
                        ...x,
                        [`${type}:${slot}`]: option.name,
                     }))
                  }}
               />
            )}
            {editTab !== 'cadence' && (slotEdited || pb.origin === 'mine') && (
               <label
                  className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[13px] text-[#9ca3af]"
                  data-testid={`cf-own-${slot}`}
               >
                  <span>
                     {slotEdited
                        ? 'Saved as your own copy, named'
                        : 'Your own copy, named'}
                  </span>
                  <input
                     value={ownName}
                     onChange={(e) =>
                        updatePlaybook((p) => ({
                           ...p,
                           [nameKey]: e.target.value,
                        }))
                     }
                     placeholder="Name this version"
                     className="h-9 px-3 rounded-lg bg-white/[0.06] border border-white/[0.12] focus:border-[#9061DA] text-sm text-white outline-none w-full sm:w-[340px]"
                     data-testid={`cf-own-${slot}-name`}
                  />
                  <span className="text-xs text-[#818181]">
                     Renaming later makes another copy; both stay in your
                     library.
                  </span>
               </label>
            )}
            {editTab === 'cadence' && (
               <TimingTab
                  pb={pb}
                  update={updatePlaybook}
                  focus={focus}
                  liveId={run.item ? run.item.key.replace(/-vm$/, '') : null}
               />
            )}
            {editTab === 'script' && (
               <ScriptTab
                  key={type}
                  pb={pb}
                  update={updatePlaybook}
                  focus={() =>
                     focus(
                        sortTouches(pb.touches).find((t) => t.action === 'call')
                           ?.id ?? ''
                     )
                  }
               />
            )}
            {editTab === 'texts' && (
               <TextsTab
                  key={type}
                  pb={pb}
                  update={updatePlaybook}
                  focus={focus}
               />
            )}
            {editTab === 'voicemails' && (
               <VoicemailsTab
                  key={type}
                  pb={pb}
                  update={updatePlaybook}
                  focus={focus}
               />
            )}
         </div>
         <StepFooter
            left={
               changed ? (
                  <button
                     type="button"
                     onClick={discardEdits}
                     className="sm:w-[160px] h-12 rounded-full bg-white/[0.08] hover:bg-white/[0.12] text-[15px] font-semibold shrink-0"
                     data-testid="cf-discard"
                  >
                     Undo changes
                  </button>
               ) : undefined
            }
            hint={
               changed
                  ? 'Saved as your own version. The original stays available.'
                  : undefined
            }
            primary={changed ? 'Save changes' : 'Done'}
            onPrimary={onDone}
            busy={busy}
            testId="cf-save-playbook"
         />
         <LeadPhoneDrawer
            open={phoneOpen}
            onClose={() => setPhoneOpen(false)}
            run={run}
         />
      </>
   )
}

/** The words one picker governs, to tell when they were edited. */
const slotWords = (p: IPlaybook, slot: TContentSlot) =>
   JSON.stringify(
      slot === 'script'
         ? p.script.map((x) => [x.title, x.text])
         : [p.texts.map((x) => x.text), p.voicemails.map((x) => x.text)]
   )

/* ---------- Whose playbook ---------- */

/**
 * With buyers and sellers (or renters) in one campaign, each has its own
 * script, texts and voicemails. The user picks one, then edits it.
 */
const LeadTypeChooser = ({
   types,
   playbook,
   edited,
   onPick,
   onDone,
   busy,
   changed,
}: {
   types: TLeadType[]
   playbook: IPlaybook
   edited: TLeadType[]
   onPick: (t: TLeadType) => void
   onDone: () => void
   busy: boolean
   changed: boolean
}) => (
   <>
      <div
         className="flex-1 min-h-[360px] px-5 py-10 flex flex-col items-center justify-center gap-6 text-center"
         data-testid="cf-type-chooser"
      >
         <div className="flex flex-col gap-1.5">
            <h2 className="text-2xl font-semibold tracking-[-.02em]">
               Whose playbook?
            </h2>
            <p className="text-sm text-[#9ca3af]">
               Each has its own script, texts and voicemails.
            </p>
         </div>
         <div className="flex flex-wrap justify-center gap-3">
            {types.map((t) => {
               const mark = edited.includes(t)
                  ? 'Edited'
                  : contentOf(playbook, t).origin === 'mine'
                    ? 'Yours'
                    : null
               return (
                  <button
                     key={t}
                     type="button"
                     onClick={() => onPick(t)}
                     className="h-12 min-w-[140px] px-6 rounded-full border border-white/[0.14] bg-white/[0.04] hover:border-[#9061DA] hover:bg-[#9061DA]/15 text-[15px] font-semibold flex items-center justify-center gap-2 transition-colors"
                     data-testid={`cf-pick-${t}`}
                  >
                     {LEAD_TYPE_LABEL[t]}
                     {mark && (
                        <span
                           className={cn(
                              'text-[11px] px-2 h-5 rounded-full flex items-center',
                              mark === 'Edited'
                                 ? 'bg-[#F5A524]/20 text-[#F5C97A]'
                                 : 'bg-[#9061DA]/25 text-[#DAC2FF]'
                           )}
                        >
                           {mark}
                        </span>
                     )}
                  </button>
               )
            })}
         </div>
      </div>
      <StepFooter
         primary={changed ? 'Save changes' : 'Done'}
         onPrimary={onDone}
         busy={busy}
         testId="cf-save-playbook"
      />
   </>
)

/* ---------- Timing ---------- */

const TimingTab = ({
   pb,
   update,
   focus,
   liveId,
}: {
   pb: IPlaybook
   update: TUpdate
   focus: (key: string) => void
   liveId: string | null
}) => {
   const [selected, setSelected] = useState<string | null>(null)
   const setTouches = (next: ITouch[]) =>
      update((p) => syncMessages({ ...p, preset: 'custom', touches: next }))
   /** A new call or text, on a given day or after the last one; later than anything already that day. */
   const add = (action: ITouch['action'], day = nextDay(pb.touches)) => {
      const id = uid(action === 'call' ? 'c' : 's')
      const last = pb.touches
         .filter((t) => t.day === day)
         .map((t) => t.time)
         .sort()
         .at(-1)
      const [h, m] = (last ?? '09:30').split(':').map(Number)
      const mins = Math.min(19 * 60, h * 60 + m + 30)
      const time = `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`
      setTouches([
         ...pb.touches,
         {
            id,
            action,
            day,
            time,
            ...(action === 'call' ? { voicemail: false } : {}),
         },
      ])
      setSelected(id)
   }
   const sorted = sortTouches(pb.touches)
   const sel = pb.touches.find((t) => t.id === selected)
   const selLabel = sel
      ? `${sel.action === 'call' ? 'Call' : 'Text'} ${sorted.filter((t) => t.action === sel.action).indexOf(sel) + 1}`
      : ''

   return (
      <div className="flex flex-col gap-3" data-testid="cf-cadence">
         <div className="flex flex-wrap items-center gap-2">
            {(
               Object.keys(CADENCE_PRESETS) as Exclude<
                  TCadencePreset,
                  'custom'
               >[]
            ).map((k) => (
               <button
                  key={k}
                  type="button"
                  onClick={() => update((p) => applyPreset(p, k))}
                  data-testid={`cf-preset-${k}`}
                  className={cn(
                     'h-9 px-3.5 rounded-full border text-[13px] font-semibold flex items-center gap-1.5',
                     pb.preset === k
                        ? 'border-[#9061DA] bg-[#9061DA]/20 text-white'
                        : 'border-white/[0.12] text-[#9ca3af] hover:text-white'
                  )}
               >
                  {CADENCE_PRESETS[k].label}
                  {k === 'balanced' && (
                     <FlowIcon
                        name="trend"
                        size={12}
                        className="text-[#00d857]"
                     />
                  )}
               </button>
            ))}
            {pb.preset === 'custom' && (
               <span className="h-9 px-3.5 rounded-full border border-[#9061DA] bg-[#9061DA]/20 text-[13px] font-semibold flex items-center">
                  Custom
               </span>
            )}
            <span className="ml-auto text-xs text-[#9ca3af]">
               {pb.preset === 'custom'
                  ? 'Your own timing'
                  : CADENCE_PRESETS[pb.preset].blurb}
            </span>
         </div>
         <CadenceTimeline
            touches={pb.touches}
            onChange={setTouches}
            selectedId={selected}
            onSelect={(id) => {
               setSelected(id)
               focus(id)
            }}
            liveId={liveId}
            onAdd={(day, action) => add(action, day)}
         />
         {sel ? (
            <TouchDetails
               touch={sel}
               label={selLabel}
               canRemove={pb.touches.length > 1}
               onChange={(t) =>
                  setTouches(pb.touches.map((x) => (x.id === t.id ? t : x)))
               }
               onRemove={() => (
                  setTouches(pb.touches.filter((x) => x.id !== sel.id)),
                  setSelected(null)
               )}
            />
         ) : (
            <p className="text-xs text-[#818181]">
               Click a call or text to change its day. Hover a day to add a call
               or text on it.
            </p>
         )}

         <div
            className="flex gap-3 items-start rounded-xl bg-white/[0.04] border border-white/[0.06] px-3.5 py-3 text-[13px] leading-[19px] text-[#9ca3af]"
            data-testid="cf-heatmap-note"
         >
            <FlowIcon
               name="clock"
               size={15}
               className="text-[#DAC2FF] mt-0.5 shrink-0"
            />
            <div>
               <b className="font-semibold text-white">
                  You pick the day, we pick the hour.
               </b>{' '}
               Each step goes out when that lead is most likely to answer, based
               on the heatmap of when leads like them pick up. Calls stay inside
               your calling hours. Day 1 is the day a lead comes in.
            </div>
         </div>
      </div>
   )
}

/* ---------- Call script ---------- */

const ScriptTab = ({
   pb,
   update,
   focus,
}: {
   pb: IPlaybook
   update: TUpdate
   focus: () => void
}) => {
   const [editing, setEditing] = useState<string | null>(null)
   const set = (id: string, text: string) =>
      update((p) => ({
         ...p,
         script: p.script.map((s) => (s.id === id ? { ...s, text } : s)),
      }))
   return (
      <div className="flex flex-col gap-2.5" data-testid="cf-script">
         <div className="flex items-center gap-2 text-xs text-[#9ca3af]">
            The caller follows these points and adapts to what the lead says.
         </div>
         <EditableText
            label="Opening line"
            text={pb.opening ?? ''}
            placeholder="The first thing the caller says, e.g. Hi {first name}, this is {assistant name}."
            editing={editing === 'opening'}
            onEdit={() => {
               setEditing('opening')
               focus()
            }}
            onDone={() => setEditing(null)}
            onChange={(opening) => update((p) => ({ ...p, opening }))}
            testId="cf-script-opening"
         />
         {pb.script.map((s, i) =>
            s.protected ? (
               <details
                  key={s.id}
                  className="rounded-xl bg-white/[0.03] border border-white/[0.06] px-4 py-2.5 text-sm"
                  data-testid={`cf-script-${i}`}
               >
                  <summary className="cursor-pointer text-[13px] text-[#9ca3af] select-none">
                     {i + 1} · {s.title}
                     <span className="ml-2 text-[11px] text-[#818181]">
                        set by Forward Flow
                     </span>
                  </summary>
                  <pre className="mt-2 whitespace-pre-wrap break-words font-sans text-[13px] leading-5 text-white/80 max-h-[320px] overflow-auto">
                     {s.text}
                  </pre>
               </details>
            ) : (
               <EditableText
                  key={s.id}
                  label={`${i + 1} · ${s.title || 'Untitled point'}`}
                  text={s.text}
                  placeholder="What should the caller say here?"
                  titleText={s.title}
                  onRename={(title) =>
                     update((p) => ({
                        ...p,
                        script: p.script.map((x) =>
                           x.id === s.id ? { ...x, title } : x
                        ),
                     }))
                  }
                  editing={editing === s.id}
                  onEdit={() => {
                     setEditing(s.id)
                     focus()
                  }}
                  onDone={() => {
                     setEditing(null)
                     /* A point left empty is not kept: the library refuses an empty section. */
                     if (!s.text.trim() && pb.script.length > 1)
                        update((p) => ({
                           ...p,
                           script: p.script.filter((x) => x.id !== s.id),
                        }))
                  }}
                  onChange={(text) => set(s.id, text)}
                  onRemove={
                     pb.script.length > 1
                        ? () =>
                             update((p) => ({
                                ...p,
                                script: p.script.filter((x) => x.id !== s.id),
                             }))
                        : undefined
                  }
                  testId={`cf-script-${i}`}
               />
            )
         )}
         <AddRow
            label="Add a point"
            hint="like handling “I already have an agent”"
            onClick={() => {
               const id = uid('sec')
               update((p) => ({
                  ...p,
                  script: [...p.script, { id, title: 'New point', text: '' }],
               }))
               setEditing(id)
            }}
            testId="cf-add-section"
         />
      </div>
   )
}

/* ---------- Texts ---------- */

const TextsTab = ({
   pb,
   update,
   focus,
}: {
   pb: IPlaybook
   update: TUpdate
   focus: (key: string) => void
}) => {
   const [editing, setEditing] = useState<string | null>(null)
   const fill = useFill()
   const textTouches = sortTouches(pb.touches).filter(
      (t) => t.action === 'text'
   )
   /* Removing a text leaves the timing alone: the next text moves up. */
   const remove = (i: number) =>
      update((p) => ({
         ...p,
         texts: p.texts.filter((_, j) => j !== i),
      }))
   const extra = textTouches.length - pb.texts.length
   return (
      <div className="flex flex-col gap-2.5" data-testid="cf-texts">
         <p className="text-xs text-[#9ca3af]">
            Sent in this order, one per text in your timing
            {extra > 0
               ? `; the last one goes out again for the ${plural(extra, 'extra text')} your timing has.`
               : '.'}
         </p>
         {pb.texts.map((m, i) => {
            const touch = textTouches[i]
            const seg = smsSegments(fill(m.text))
            return (
               <EditableText
                  key={m.id}
                  label={`Text ${i + 1}${touch ? ` · Day ${touch.day}` : ' · not in your timing yet'}`}
                  meta={
                     seg > 1 ? (
                        <span className="text-amber-400">
                           long, sends as {seg} texts
                        </span>
                     ) : undefined
                  }
                  text={m.text}
                  editing={editing === m.id}
                  onEdit={() => {
                     setEditing(m.id)
                     if (touch) focus(touch.id)
                  }}
                  onDone={() => setEditing(null)}
                  onChange={(text) =>
                     update((p) => ({
                        ...p,
                        texts: p.texts.map((x) =>
                           x.id === m.id ? { ...x, text } : x
                        ),
                     }))
                  }
                  onRemove={pb.texts.length > 1 ? () => remove(i) : undefined}
                  testId={`cf-text-${i}`}
               />
            )
         })}
         <AddRow
            label="Add a text"
            hint="sent by the next text in your timing; add one there if you need it"
            onClick={() =>
               update((p) => ({
                  ...p,
                  texts: [
                     ...p.texts,
                     {
                        id: uid('s'),
                        text: 'Hi {first name}, {assistant name} again from {organization}. Happy to help whenever you are ready.',
                     },
                  ],
               }))
            }
            testId="cf-add-text-msg"
         />
         <EditableText
            label={
               <span className="flex items-center gap-1.5">
                  <FlowIcon name="spark" size={12} /> When they text back
               </span>
            }
            text={pb.replyInstructions}
            editing={editing === 'reply'}
            onEdit={() => setEditing('reply')}
            onDone={() => setEditing(null)}
            onChange={(text) =>
               update((p) => ({ ...p, replyInstructions: text }))
            }
            muted
            testId="cf-reply-instructions"
         />
      </div>
   )
}

/* ---------- Voicemails ---------- */

const VoicemailsTab = ({
   pb,
   update,
   focus,
}: {
   pb: IPlaybook
   update: TUpdate
   focus: (key: string) => void
}) => {
   const [editing, setEditing] = useState<string | null>(null)
   const calls = sortTouches(pb.touches).filter((t) => t.action === 'call')
   const withVm = calls.filter((t) => t.voicemail)
   const setVm = (id: string, on: boolean) =>
      update((p) =>
         syncMessages({
            ...p,
            preset: 'custom',
            touches: p.touches.map((t) =>
               t.id === id ? { ...t, voicemail: on } : t
            ),
         })
      )
   return (
      <div className="flex flex-col gap-2.5" data-testid="cf-voicemails">
         <p className="text-xs text-[#9ca3af]">
            Left only when a call isn’t answered, in the voice you picked.
         </p>
         {withVm.length > pb.voicemails.length && (
            <p className="text-xs text-[#818181]">
               The last voicemail is left again for the{' '}
               {plural(withVm.length - pb.voicemails.length, 'extra call')} your
               timing has.
            </p>
         )}
         {pb.voicemails.map((v, i) => (
            <EditableText
               key={v.id}
               label={
                  withVm[i]
                     ? `After call ${calls.indexOf(withVm[i]) + 1}`
                     : `Voicemail ${i + 1} · no call leaves it yet`
               }
               text={v.text}
               editing={editing === v.id}
               onEdit={() => {
                  setEditing(v.id)
                  if (withVm[i]) focus(`${withVm[i].id}-vm`)
               }}
               onDone={() => setEditing(null)}
               onChange={(text) =>
                  update((p) => ({
                     ...p,
                     voicemails: p.voicemails.map((x) =>
                        x.id === v.id ? { ...x, text } : x
                     ),
                  }))
               }
               onRemove={
                  pb.voicemails.length > 1
                     ? () =>
                          update((p) => ({
                             ...p,
                             voicemails: p.voicemails.filter(
                                (x) => x.id !== v.id
                             ),
                          }))
                     : undefined
               }
               testId={`cf-vm-${i}`}
            />
         ))}
         {calls
            .filter((t) => !t.voicemail)
            .map((t) => (
               <AddRow
                  key={t.id}
                  label={`Add a voicemail after call ${calls.indexOf(t) + 1}`}
                  onClick={() => setVm(t.id, true)}
                  testId={`cf-add-vm-${t.id}`}
               />
            ))}
      </div>
   )
}
